import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useApi, type PropertyResponse } from '@/shared/api';
import { useObjectScope } from '@/shared/object-scope';
import {
  applyLocalFilters,
  propertiesSearchFromState,
  propertyFiltersFromSearch,
  propertyPageFromSearch,
  type PropertiesSearch,
} from '../propertiesListSearch';
import { PROPERTIES_PAGE_SIZE, propertiesListArgs } from '../usePropertiesList';

export interface PropertyNeighbour {
  id: string;
  name: string;
  /** Die Seite, auf der die Liste dieses Apartment zeigt — sie wandert im Sprung mit. */
  search: PropertiesSearch;
}

/**
 * Die Nachbarn eines Apartments in genau der Reihenfolge, die die Liste gerade zeigt (F4).
 *
 * <p>Der Arbeitskontext hängt an der URL: kam der Nutzer aus einer gefilterten oder gesuchten
 * Liste, trägt der Aufruf der Detailseite deren Suchparameter, und die Nachbarschaft wird aus
 * derselben Abfrage gebildet — gleicher Schlüssel, also ein Treffer im Cache statt eines zweiten
 * Roundtrips. Ohne Suchparameter gilt die Vorbelegung der Liste.</p>
 *
 * <p>An der Grenze einer Seite wird die Nachbarseite nachgeladen: ein Apartment auf Seite 2 steht
 * in der Liste, nur nicht auf dem aktuellen Ausschnitt. Ist auch dort keins mehr, ist der Pfeil
 * deaktiviert — es wird nicht umgelaufen, weil sonst verwischt, wo die Liste endet.</p>
 *
 * <p>Zeigt die Liste das aufgerufene Apartment gar nicht (etwa ein archiviertes bei der
 * Vorbelegung „Aktiv"), gibt es keine Nachbarschaft. Dann liefert der Haken `inList: false`, und
 * die Pfeile treten ganz zurück, statt zwei tote Schaltflächen zu zeigen.</p>
 */
export function usePropertyNeighbours(currentId: string) {
  const api = useApi();
  const search = useSearch({ strict: false }) as PropertiesSearch;
  const { propertyIds } = useObjectScope();

  const filters = propertyFiltersFromSearch(search);
  const page = propertyPageFromSearch(search);
  const scope = propertyIds ?? null;

  const pageQuery = useQuery({
    queryKey: ['properties', 'list', propertiesListArgs({ page, filters, propertyIds: scope })],
    queryFn: () => api.properties.list(propertiesListArgs({ page, filters, propertyIds: scope })),
    staleTime: 60_000,
  });

  /** Genau der Ausschnitt, aus dem der Nutzer kam — auch der Rückweg trägt ihn. */
  const listSearch = propertiesSearchFromState({ filters, page });
  const items = applyLocalFilters(pageQuery.data?.items ?? [], filters);
  const index = items.findIndex((p) => p.id === currentId);
  const total = pageQuery.data?.total ?? 0;

  const needsPrevPage = index === 0 && page > 1;
  const needsNextPage =
    index === items.length - 1 && index >= 0 && page * PROPERTIES_PAGE_SIZE < total;

  const prevPageQuery = useQuery({
    queryKey: [
      'properties',
      'list',
      propertiesListArgs({ page: page - 1, filters, propertyIds: scope }),
    ],
    queryFn: () =>
      api.properties.list(propertiesListArgs({ page: page - 1, filters, propertyIds: scope })),
    enabled: needsPrevPage,
    staleTime: 60_000,
  });

  const nextPageQuery = useQuery({
    queryKey: [
      'properties',
      'list',
      propertiesListArgs({ page: page + 1, filters, propertyIds: scope }),
    ],
    queryFn: () =>
      api.properties.list(propertiesListArgs({ page: page + 1, filters, propertyIds: scope })),
    enabled: needsNextPage,
    staleTime: 60_000,
  });

  const toNeighbour = (
    p: PropertyResponse | undefined,
    onPage: number,
  ): PropertyNeighbour | null =>
    p
      ? {
          id: p.id,
          name: p.adminDisplayName ?? p.name,
          search: propertiesSearchFromState({ filters, page: onPage }),
        }
      : null;

  if (index < 0) {
    return {
      inList: false,
      loading: pageQuery.isLoading,
      listSearch,
      previous: null,
      next: null,
    } as const;
  }

  const prevOnPage = index > 0 ? items[index - 1] : undefined;
  const nextOnPage = index < items.length - 1 ? items[index + 1] : undefined;
  const prevOnPrevPage = needsPrevPage
    ? applyLocalFilters(prevPageQuery.data?.items ?? [], filters).at(-1)
    : undefined;
  const nextOnNextPage = needsNextPage
    ? applyLocalFilters(nextPageQuery.data?.items ?? [], filters).at(0)
    : undefined;

  return {
    inList: true,
    loading: pageQuery.isLoading,
    listSearch,
    previous: toNeighbour(prevOnPage, page) ?? toNeighbour(prevOnPrevPage, page - 1),
    next: toNeighbour(nextOnPage, page) ?? toNeighbour(nextOnNextPage, page + 1),
  } as const;
}
